import { computed } from 'vue';
import { getI18n, t } from '@/utils/i18n-helper';
import { normalizeAmount } from '@/composables/front/perfil/monedero.utils';
import { useMonedero } from '@/composables/front/perfil/useMonedero';

export function usePerfilBalance() {
    const i18n = getI18n();

    const {
        movimientos,
        total,
        loading,
        error,
        fetchMonedero,
    } = useMonedero();

    const currentLocale = computed(() => i18n?.locale || 'es');

    const integerFormatter = computed(() => new Intl.NumberFormat(currentLocale.value, {
        maximumFractionDigits: 0,
    }));

    const balanceColumns = computed(() => {
        const locale = currentLocale.value;

        return [
            {
                key: 'date',
                label: locale && t('perfil.fecha', {}, 'Fecha'),
            },
            {
                key: 'description',
                label: locale && t('perfil.monedero_columna_descripcion', {}, 'Descripción'),
            },
            {
                key: 'amount',
                label: locale && t('perfil.monedero_columna_unidades', {}, 'Unidades'),
                align: 'right',
            },
        ];
    });

    const titleFromDescription = description => {
        if (!description) return t('perfil.monedero_movimiento_saldo', {}, 'MOVIMIENTO DE SALDO');

        return String(description).trim().toLocaleUpperCase(currentLocale.value);
    };

    const subtitleFromDate = value => {
        if (!value) return t('perfil.monedero_sin_fecha', {}, 'SIN FECHA');

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return String(value).toLocaleUpperCase(currentLocale.value);
        }

        return new Intl.DateTimeFormat(currentLocale.value, {
            day:   '2-digit',
            month: 'short',
            year:  'numeric',
        }).format(parsed).toLocaleUpperCase(currentLocale.value);
    };

    const formatUnits = value => `${integerFormatter.value.format(Math.round(value))} U`;

    const formatSignedUnits = value => {
        const roundedValue = Math.round(value);

        if (roundedValue > 0) {
            return `+ ${formatUnits(roundedValue)}`;
        }

        if (roundedValue < 0) {
            return `- ${formatUnits(Math.abs(roundedValue))}`;
        }

        return formatUnits(roundedValue);
    };

    const totalUnitsValueLabel = computed(() => integerFormatter.value.format(Math.round(total.value)));

    const balanceSummaryLabel = computed(() => t('perfil.monedero_resumen_unidades', {
        total: `${totalUnitsValueLabel.value} U`,
    }, `${totalUnitsValueLabel.value} U disponibles en tu monedero`));

    const transactions = computed(() => movimientos.value.map((movimiento, index) => {
        const amount = normalizeAmount(movimiento?.importe);
        const isPositive = amount > 0;
        const isNegative = amount < 0;

        return {
            id: movimiento?.id ?? `movimiento-${index}`,
            date: subtitleFromDate(
                movimiento?.created_at
                ?? movimiento?.updated_at
                ?? movimiento?.fecha
                ?? movimiento?.fecha_creacion
                ?? movimiento?.date
            ),
            description: titleFromDescription(movimiento?.descripcion),
            amount: formatSignedUnits(amount),
            amountTone: isPositive ? 'positive' : isNegative ? 'negative' : 'neutral',
        };
    }));

    const transactionsTableTitle = computed(() => {
        const locale = currentLocale.value;

        return transactions.value.length > 0
            ? locale && t('perfil.monedero_historial', {}, 'Historial de transacciones')
            : '';
    });

    return {
        fetchMonedero,
        loading,
        error,
        transactions,
        balanceColumns,
        transactionsTableTitle,
        totalUnitsValueLabel,
        balanceSummaryLabel,
    };
}
